// src/components/UserPicker.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { usersApi } from "../api/usersApi";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import Input from "../shared/ui/Input";
import { SkeletonList } from "../shared/ui";

// Search-by-name/email combobox — replaces asking an admin to type a raw
// numeric user id they'd have no way to look up. Debounced, server-side
// search (page_size=20) while open, rather than fetching the whole org's
// user list once and filtering client-side.
//
// Pass `users` to scope the choices to a caller-provided list instead (e.g.
// "grant a project role" only makes sense for people already on the
// engagement's team) — that list stays a client-side filter, since it's
// already local and typically small (one engagement's roster).
export default function UserPicker({
  value,
  onChange,
  users: providedUsers,
  placeholder = "Search by name or email…",
}) {
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // Both current callers only ever set `value` via this component's own
  // onChange (see AccountRoleGrantModal/TeamMemberFormModal) — so caching
  // the picked user's own record here is enough to keep it displayed even
  // after a later search's results no longer include them.
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (providedUsers != null || !open) return;
    let cancelled = false;
    setLoading(true);
    usersApi
      .list({ search: debouncedQuery || undefined, pageSize: 20 })
      .then(({ data }) => {
        if (!cancelled) setFetchedUsers(data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setFetchedUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [providedUsers, open, debouncedQuery]);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const users = providedUsers ?? fetchedUsers;
  const selected = selectedUser ?? users.find((u) => u.id === value);

  // providedUsers is a small, already-local list — filter it client-side
  // rather than adding a server round trip for something already in memory.
  const filteredProvided = useMemo(() => {
    if (providedUsers == null) return null;
    const q = query.trim().toLowerCase();
    if (!q) return providedUsers;
    return providedUsers.filter(
      (u) =>
        (u.username || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
    );
  }, [providedUsers, query]);

  const options = filteredProvided ?? users;

  return (
    <div className="relative" ref={ref}>
      <Input
        placeholder={loading ? "Loading users…" : placeholder}
        value={open ? query : selected ? `${selected.username} (${selected.email})` : ""}
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        onChange={(e) => setQuery(e.target.value)}
      />
      {open && (
        <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-line rounded-card shadow-card py-1">
          {loading ? (
            <li className="px-3 py-2">
              <SkeletonList rows={4} />
            </li>
          ) : (
            <>
              {options.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
                    onClick={() => {
                      onChange(u.id);
                      setSelectedUser(u);
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <span className="text-sm font-medium text-ink truncate">{u.username}</span>
                    <span className="text-xs text-muted truncate">{u.email}</span>
                  </button>
                </li>
              ))}
              {options.length === 0 && (
                <li className="px-3 py-2 text-sm text-muted">No matches</li>
              )}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
